import * as React from 'react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { KIND, SHAPE } from 'baseui/button'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import ModalCustomSize from '@/components/ShowModal/ModalCustomSize'
import Icons from '@/scenes/Editor/components/Icons'
import { ResizeSizeRow } from './ResizeSizeRow'
import type { Unit } from '@/utils/unit'

interface CustomSizeButtonProps {
  active?: boolean
  initialWidth?: number
  initialHeight?: number
  initialUnit?: Unit
  initialAspectRatio?: number
  /** Controlled overrides — lets editor keep the row in sync with frame/undo */
  displayWidth?: number
  displayHeight?: number
  displayUnit?: Unit
  dimensionSeparator?: string
  requirePremium?: boolean
  onPremiumRequired?: (proceed: () => void) => void
  onClick?: () => void
  onApply: (result: { width: number; height: number; unit: Unit }) => void
  premiumBadge?: React.ReactNode
  expandedContent?: React.ReactNode
  className?: string
}

export function CustomSizeButton({
  active = false,
  initialWidth = 0,
  initialHeight = 0,
  initialUnit = 'px',
  initialAspectRatio = 1,
  displayWidth,
  displayHeight,
  displayUnit,
  dimensionSeparator = 'x',
  requirePremium = false,
  onPremiumRequired,
  onClick,
  onApply,
  premiumBadge,
  expandedContent,
  className,
}: CustomSizeButtonProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [unit, setUnit] = useState<Unit>(initialUnit)
  const [aspectRatio, setAspectRatio] = useState(initialAspectRatio)
  const [customSize, setCustomSize] = useState({ width: initialWidth, height: initialHeight })

  useEffect(() => {
    if (open) return
    if (displayWidth !== undefined) setCustomSize(s => ({ ...s, width: displayWidth }))
    if (displayHeight !== undefined) setCustomSize(s => ({ ...s, height: displayHeight }))
    if (displayUnit !== undefined) setUnit(displayUnit)
    if (displayWidth !== undefined && displayHeight !== undefined && displayHeight > 0) {
      setAspectRatio(displayWidth / displayHeight)
    }
  }, [displayWidth, displayHeight, displayUnit, open])

  const shownWidth = displayWidth ?? customSize.width
  const shownHeight = displayHeight ?? customSize.height
  const shownUnit = displayUnit ?? unit

  const openModal = () => {
    if (requirePremium && onPremiumRequired) {
      onPremiumRequired(() => setOpen(true))
      return
    }
    setOpen(true)
  }

  const handleRowClick = () => {
    onClick?.()
    openModal()
  }

  const description =
    active && (shownWidth || shownHeight)
      ? `${shownWidth} ${dimensionSeparator} ${shownHeight} ${t(shownUnit)}`
      : undefined

  const adjustButton = active ? (
    <ButtonCustom
      shape={SHAPE.square}
      kind={KIND.minimal}
      type={SizeButton.SMALL}
      onClick={(e) => {
        e.stopPropagation()
        if (requirePremium && onPremiumRequired) {
          onPremiumRequired(() => setOpen(true))
          return
        }
        setOpen(true)
      }}
    >
      <Icons.Adjust size={24} />
    </ButtonCustom>
  ) : null

  return (
    <>
      <ResizeSizeRow
        name={t('Custom size')}
        description={description}
        icon={Icons.CustomSize}
        active={active}
        onClick={handleRowClick}
        premiumBadge={premiumBadge}
        rightAdornment={adjustButton}
        appendUnitLabel={false}
        className={className}
        expandedContent={expandedContent}
      />
      <ModalCustomSize
        isOpen={open}
        onClose={() => setOpen(false)}
        customSize={customSize}
        setCustomSize={setCustomSize}
        applyCustomSize={({ width, height }) => {
          onApply({ width, height, unit })
        }}
        aspectRatio={aspectRatio}
        setAspectRatio={setAspectRatio}
        unit={unit}
        setUnit={(u: any) => setUnit(u)}
      />
    </>
  )
}
